import * as THREE from 'three';
import { CSG } from 'three-csg-ts';
import { InsideType, ModuleType } from '../../../constants/enum';
import { GeometryUtils } from '../../../utils/geometry/GeometryUtils';
import { all } from 'axios';
import { set } from 'lodash';

export default class CreateGroupPlane {
  constructor(sceneManager) {
    this.sceneManager = sceneManager;
    this.proximityThreshold = 0.01; // Giảm để chỉ nhận diện khi thực sự sát nhau
    this.rotationTolerance = 0.01; // Tolerance cho góc quay (radian)
    this.alignmentTolerance = 0.01; // Tolerance cho thẳng hàng
    this.sizeTolerance = 0.01; // Tolerance cho kích thước mặt tiếp xúc
    this.groupPlanes = new Map();
    this.moduleGroups = new Map();
    this.planePadding = 0.3;
    this.backsplashHeight = 0.65; // Chiều cao vách bếp
    this.countertopHeight = 0.04; // Độ dày mặt bếp
    this.countertopThickness = 0.04; // Độ dày mặt bếp
    this.countertopOverhang = 0.02; // Phần nhô ra của mặt bếp so với module
    this.countertopColor = 0xdfdfde; // Màu mặt bếp
    this.backsplashColor = 0xdfdfde; // Màu vách bếp
    this.backsplashOpacity = 1; // Độ trong vách bếp

    this.countertopRoughness = 0.6; // Độ nhám mặt bếp
    this.countertopMetalness = 0; // Độ kim loại mặt bếp

    this.backsplashRoughness = 0.25; // Độ nhám vách bếp
    this.backsplashMetalness = 0; // Độ kim loại vách bếp
  }

  // Tìm tất cả modules trong scene
  getAllKitchenLowerModules() {
    if (!this.sceneManager || !this.sceneManager.scene) return [];

    const modules = [];
    this.sceneManager.scene.traverse(object => {
      if (
        object.userData?.type === 'module' &&
        object.userData?.cabinetType === 'kitchen' &&
        object.userData?.moduleType?.includes('lower') &&
        !object.userData?.moduleType?.includes('tamopbia') &&
        (object.userData?.moduleType?.includes('sink') ||
          object.userData?.moduleType?.includes('kitchen') ||
          object.userData?.moduleType?.includes('corner'))
      ) {
        modules.push(object);
      }
    });
    return modules;
  }

  changeBacksplashHeight(height) {
    this.backsplashHeight = height;
  }

  // Lấy rotation chuẩn hóa của module (chỉ lấy Y rotation)
  getNormalizedRotation(module) {
    return module.rotation.y;
  }

  // Kiểm tra 2 module có cùng góc quay không
  hasSameRotation(module1, module2) {
    const rot1 = this.getNormalizedRotation(module1);
    const rot2 = this.getNormalizedRotation(module2);
    const diff = Math.abs(rot1 - rot2);

    // Xử lý trường hợp góc quay qua 2π
    const normalizedDiff = Math.min(diff, Math.abs(diff - 2 * Math.PI));

    return normalizedDiff <= this.rotationTolerance;
  }

  // Kiểm tra 2 module có thẳng hàng không (theo trục X hoặc Z)
  isAligned(module1, module2) {
    const pos1 = module1.position;
    const pos2 = module2.position;

    // Kiểm tra thẳng hàng theo trục X (cùng Z)
    const alignedX = Math.abs(pos1.z - pos2.z) <= this.alignmentTolerance;

    // Kiểm tra thẳng hàng theo trục Z (cùng X)
    const alignedZ = Math.abs(pos1.x - pos2.x) <= this.alignmentTolerance;

    return alignedX || alignedZ;
  }

  // Kiểm tra 2 module có cùng cao độ (theo trục Y)
  isSameHeight(module1, module2) {
    const pos1 = module1.position;
    const pos2 = module2.position;

    // Dung sai cho việc so sánh độ cao
    // const tolerance = this.alignmentTolerance || 0.001;
    const tolerance = 0.001;

    return Math.abs(pos1.y - pos2.y) <= tolerance;
  }

  // Tính kích thước mặt tiếp xúc giữa 2 module
  getContactFaceSize(module1, module2) {
    const box1 = new THREE.Box3().setFromObject(module1);
    const box2 = new THREE.Box3().setFromObject(module2);

    const pos1 = module1.position;
    const pos2 = module2.position;

    // Xác định hướng tiếp xúc
    const deltaX = Math.abs(pos1.x - pos2.x);
    const deltaZ = Math.abs(pos1.z - pos2.z);

    let contactSize1, contactSize2;

    if (deltaX > deltaZ) {
      // Tiếp xúc theo trục X (mặt YZ)
      const size1 = box1.max.clone().sub(box1.min);
      const size2 = box2.max.clone().sub(box2.min);
      contactSize1 = size1.y * size1.z;
      contactSize2 = size2.y * size2.z;
    } else {
      // Tiếp xúc theo trục Z (mặt XY)
      const size1 = box1.max.clone().sub(box1.min);
      const size2 = box2.max.clone().sub(box2.min);
      contactSize1 = size1.x * size1.y;
      contactSize2 = size2.x * size2.y;
    }

    return { size1: contactSize1, size2: contactSize2 };
  }

  // Kiểm tra kích thước mặt tiếp xúc có bằng nhau không
  hasEqualContactSize(module1, module2) {
    const module1WithoutAccessory =
      GeometryUtils.moduleWithoutAccessory(module1);
    const module2WithoutAccessory =
      GeometryUtils.moduleWithoutAccessory(module2);

    const { size1, size2 } = this.getContactFaceSize(
      module1WithoutAccessory,
      module2WithoutAccessory
    );
    const sizeDiff = Math.abs(size1 - size2);
    const avgSize = (size1 + size2) / 2;

    // So sánh theo tỷ lệ phần trăm
    return sizeDiff / avgSize <= this.sizeTolerance;
  }

  // Tính khoảng cách giữa 2 bounding box
  calculateBoxToBoxDistance(box1, box2) {
    const dx = Math.max(box1.min.x - box2.max.x, 0, box2.min.x - box1.max.x);
    const dy = Math.max(box1.min.y - box2.max.y, 0, box2.min.y - box1.max.y);
    const dz = Math.max(box1.min.z - box2.max.z, 0, box2.min.z - box1.max.z);

    return Math.sqrt(dx * dx + dy * dy + dz * dz);
  }

  // Kiểm tra xem 2 module có thể nhóm được không (tất cả điều kiện)
  canModulesGroup(module1, module2) {
    // 1. Kiểm tra khoảng cách gần nhau
    const box1 = new THREE.Box3().setFromObject(module1);
    const box2 = new THREE.Box3().setFromObject(module2);
    const distance = this.calculateBoxToBoxDistance(box1, box2);

    if (distance > this.proximityThreshold) {
      return false;
    }

    //1. Kiểm tra 1 trong 2 có module nào là tủ đặc biệt (tủ góc, tủ khuyết cột, tủ góc khuyết cột) hay không?
    if (
      module1.userData?.isSpecial ||
      module2.userData?.isSpecial ||
      module1.userData?.moduleData?.moduleType
        ?.toLowerCase()
        .includes('curved_block') ||
      module2.userData?.moduleData?.moduleType
        ?.toLowerCase()
        .includes('curved_block')
    ) {
      return false;
    }

    // 2. Kiểm tra cùng góc quay
    if (!this.hasSameRotation(module1, module2)) {
      return false;
    }

    // 3. Kiểm tra thẳng hàng
    if (!this.isAligned(module1, module2)) {
      return false;
    }

    // 4. Kiểm tra cùng cao độ
    if (!this.isSameHeight(module1, module2)) {
      return false;
    }

    // 5. Kiểm tra kích thước mặt tiếp xúc
    if (!this.hasEqualContactSize(module1, module2)) {
      return false;
    }

    return true;
  }

  // Tìm các nhóm modules bằng Union-Find algorithm để đảm bảo tính liên thông
  findModuleGroupsForCountertop(modules) {
    const groups = [];
    const visited = new Set();

    modules.forEach(module => {
      if (module.userData.moduleData?.moduleType?.includes('lower')) {
        if (!visited.has(module.uuid)) {
          const group = [];
          this.dfsGroupSearch(module, modules, group, visited);
          if (group.length > 0) {
            // Chỉ tạo group khi có ít nhất 1 module
            groups.push(group);
          }
        }
      }
    });

    return groups;
  }

  // Tìm kiếm theo chiều sâu với điều kiện nghiêm ngặt
  findModuleGroupsForBacksplash(modules) {
    const groups = [];
    const visited = new Set();

    const checkHasBacksplash = true;

    modules.forEach(module => {
      if (module.userData.moduleData?.moduleType?.includes('lower')) {
        if (!visited.has(module.uuid)) {
          const group = [];
          this.dfsGroupSearch(
            module,
            modules,
            group,
            visited,
            checkHasBacksplash
          );
          if (group.length > 0) {
            // Chỉ tạo group khi có ít nhất 1 module
            groups.push(group);
          }
        }
      }
    });

    return groups;
  }

  dfsGroupSearch(
    currentModule,
    allModules,
    group,
    visited,
    checkHasBacksplash
  ) {
    visited.add(currentModule.uuid);

    // Kiểm tra chính module hiện tại trước khi thêm vào group
    if (checkHasBacksplash) {
      const currentCabinet =
        this.sceneManager.cabinetManager.getCurrentCabinet();
      const foundCurrentModule = currentCabinet.getModuleByGroupUuid(
        currentModule.uuid
      );
      const hasBacksplash = [
        foundCurrentModule.formularController.currentOptions?.opVachSau,
        foundCurrentModule.formularController.currentOptions?.opVachTrai,
        foundCurrentModule.formularController.currentOptions?.opVachPhai,
      ].some(Boolean);

      if (!hasBacksplash) return; // Không thêm vào group nếu không có backsplash
    }

    group.push(currentModule);

    allModules.forEach(otherModule => {
      if (
        !visited.has(otherModule.uuid) &&
        this.canModulesGroup(currentModule, otherModule)
      ) {
        // Truyền checkHasBacksplash vào đệ quy để duy trì điều kiện
        this.dfsGroupSearch(
          otherModule,
          allModules,
          group,
          visited,
          checkHasBacksplash
        );
      }
    });
  }

  // Xác thực group sau khi tìm được (đảm bảo tất cả modules trong group đều thỏa mãn điều kiện với nhau)
  validateGroup(moduleGroup) {
    //Những group có 1 module thì mặc định đúng
    if (moduleGroup.length < 2) return true;

    // Kiểm tra mọi cặp module trong group
    for (let i = 0; i < moduleGroup.length - 1; i++) {
      for (let j = i + 1; j < moduleGroup.length; j++) {
        // Kiểm tra cùng rotation và alignment với module đầu tiên
        if (
          !this.hasSameRotation(moduleGroup[i], moduleGroup[0]) ||
          !this.hasSameRotation(moduleGroup[j], moduleGroup[0])
        ) {
          return false;
        }
      }
    }

    // Kiểm tra tính liên thông của group (mỗi module phải kề với ít nhất 1 module khác)
    return this.isGroupConnected(moduleGroup);
  }

  // Kiểm tra tính liên thông của group
  isGroupConnected(moduleGroup) {
    if (moduleGroup.length < 2) return false;

    // Tạo graph adjacency
    const adjacency = new Map();
    moduleGroup.forEach(module => {
      adjacency.set(module.uuid, new Set());
    });

    // Tạo kết nối giữa các module
    for (let i = 0; i < moduleGroup.length; i++) {
      for (let j = i + 1; j < moduleGroup.length; j++) {
        if (this.canModulesGroup(moduleGroup[i], moduleGroup[j])) {
          adjacency.get(moduleGroup[i].uuid).add(moduleGroup[j].uuid);
          adjacency.get(moduleGroup[j].uuid).add(moduleGroup[i].uuid);
        }
      }
    }

    // BFS để kiểm tra liên thông
    const visited = new Set();
    const queue = [moduleGroup[0].uuid];
    visited.add(moduleGroup[0].uuid);

    while (queue.length > 0) {
      const current = queue.shift();
      const neighbors = adjacency.get(current);

      neighbors.forEach(neighbor => {
        if (!visited.has(neighbor)) {
          visited.add(neighbor);
          queue.push(neighbor);
        }
      });
    }

    return visited.size === moduleGroup.length;
  }

  // Tính bounding box tổng hợp của một nhóm modules
  calculateGroupBoundingBox(moduleGroup, withPadding = false) {
    const combinedBBox = new THREE.Box3();

    moduleGroup.forEach(module => {
      const moduleBBox = new THREE.Box3().setFromObject(module);
      combinedBBox.union(moduleBBox);
    });

    if (withPadding) {
      const padding = new THREE.Vector3(
        this.planePadding,
        0,
        this.planePadding
      );
      combinedBBox.expandByVector(padding);
    }

    return combinedBBox;
  }

  removeAccessoriesFromModelGroup(moduleGroup) {
    return moduleGroup
      .map(group => {
        // Tạo group mới để không ảnh hưởng đến group gốc
        const filteredGroup = GeometryUtils.moduleWithoutAccessory(group);

        return filteredGroup;
      })
      .filter(group => {
        // Kiểm tra xem group còn children không (loại bỏ group rỗng)
        let hasNonSinkChildren = false;
        group.traverse(object => {
          if (
            object !== group &&
            !object.userData?.name?.includes('ACCESSORY')
          ) {
            hasNonSinkChildren = true;
          }
        });
        return hasNonSinkChildren;
      });
  }

  getSinkFromModelGroup(modelGroup) {
    for (const group of modelGroup) {
      let foundSink = null;

      group.traverse(child => {
        if (
          !foundSink &&
          (child.name === 'ACCESSORY_SINK' ||
            child.userData?.name === 'ACCESSORY_SINK')
        ) {
          foundSink = child;
        }
      });

      if (foundSink) {
        return foundSink;
      }
    }

    return null;
  }

  // Hàm tạo box geometry cho hole
  createHoleBox(holeScene) {
    // Tính bounding box của hole model
    const bbox = new THREE.Box3().setFromObject(holeScene);
    const holeSize = new THREE.Vector3();
    const holeCenter = new THREE.Vector3();
    bbox.getSize(holeSize);
    bbox.getCenter(holeCenter);

    // Tạo geometry đục lỗ dựa trên kích thước của hole model
    const holeGeometry = new THREE.BoxGeometry(
      holeSize.x,
      // holeSize.y,
      1,
      holeSize.z
    );

    const holeMaterial = new THREE.MeshStandardMaterial({
      color: 0x00ff00, // Màu xanh để debug
      opacity: 1,
      transparent: true,
    });

    const holeBoxMesh = new THREE.Mesh(holeGeometry, holeMaterial);

    holeBoxMesh.updateMatrixWorld(true);

    return holeBoxMesh;
  }

  // Hàm tìm tất cả model có tên "ACCESSORY_SINK" trong moduleGroup
  findAllSinkModels(moduleGroup) {
    const sinkModels = [];

    // Duyệt qua từng module trong group
    for (const module of moduleGroup) {
      // Hàm đệ quy để tìm trong tất cả children
      const findSinksRecursive = object => {
        // Kiểm tra userData.type của object hiện tại
        if (
          object.userData.isInside &&
          (object.userData.type === InsideType.ACCESSORY_SINK ||
            object.userData.type === InsideType.ACCESSORY_FAUCET ||
            object.userData.type === InsideType.ACCESSORY_COOKER)
        ) {
          sinkModels.push(object);
        }

        // Tìm trong children
        for (const child of object.children) {
          findSinksRecursive(child);
        }
      };

      findSinksRecursive(module);
    }

    return sinkModels;
  }

  // addCountertopFromMeshTemplate(module) {
  //   const countertopMeshes = [];
  //   module.traverse(child => {
  //     if (
  //       child.isMesh &&
  //       child.userData.name &&
  //       child.userData.name.toUpperCase().includes('MAT-DA')
  //     ) {
  //       if (child.scale.x === 0 || child.scale.y === 0 || child.scale.z === 0) {
  //         return;
  //       }
  //       countertopMeshes.push(child);
  //     }
  //   });

  //   if (countertopMeshes.length === 0) {
  //     console.warn("Không tìm thấy mesh nào có tên chứa 'MAT-DA'");
  //     return null;
  //   }

  //   const countertopGroup = new THREE.Group();
  //   countertopGroup.name = 'SINGLE_COUNTERTOP';
  //   countertopGroup.userData.name = 'SINGLE_COUNTERTOP';

  //   countertopMeshes.forEach(mesh => {
  //     mesh.updateMatrixWorld(true);

  //     const worldPos = new THREE.Vector3();
  //     const worldQuat = new THREE.Quaternion();
  //     const worldScale = new THREE.Vector3();
  //     mesh.matrixWorld.decompose(worldPos, worldQuat, worldScale);

  //     const clonedGeometry = mesh.geometry.clone();

  //     clonedGeometry.computeBoundingBox();
  //     const bb = clonedGeometry.boundingBox;
  //     const geometryHeight = bb.max.y - bb.min.y;
  //     const newScaleY =
  //       geometryHeight > 0 ? this.countertopThickness / geometryHeight : 1;

  //     //  Recompute UV dựa trên kích thước thực tế sau scale
  //     // để texture không bị kéo dãn
  //     const realSizeX = (bb.max.x - bb.min.x) * worldScale.x;
  //     const realSizeZ = (bb.max.z - bb.min.z) * worldScale.z;
  //     this._recomputeUV(clonedGeometry, bb, realSizeX, realSizeZ);

  //     const planeMaterial = new THREE.MeshStandardMaterial({
  //       color: this.countertopColor,
  //       opacity: 1,
  //       transparent: false,
  //       side: THREE.FrontSide,
  //       roughness: this.countertopRoughness,
  //       metalness: this.countertopMetalness,
  //       flatShading: false,
  //       polygonOffset: true,
  //       polygonOffsetFactor: -1,
  //       polygonOffsetUnits: -1,
  //     });

  //     // ✅ Copy texture từ mesh gốc nếu có
  //     if (mesh.material?.map) {
  //       planeMaterial.map = mesh.material.map;
  //       planeMaterial.map.needsUpdate = true;
  //     }

  //     const countertop = new THREE.Mesh(clonedGeometry, planeMaterial);
  //     countertop.castShadow = true;
  //     countertop.receiveShadow = true;

  //     countertop.position.copy(worldPos);
  //     countertop.position.y -= this.countertopThickness / 2;
  //     countertop.quaternion.copy(worldQuat);
  //     countertop.scale.set(worldScale.x, newScaleY, worldScale.z);

  //     countertop.userData = {
  //       ...countertop.userData,
  //       ...(this.sceneManager.countertop.texture ?? {}),
  //     };
  //     countertop.userData.type = ModuleType.COUNTERTOP;
  //     countertop.userData.requireCollision = true;

  //     const isCorner = module.userData.moduleType?.includes('corner');
  //     if (isCorner) {
  //       countertop.userData.name = mesh.userData.name;
  //       countertop.userData.countertopDepth =
  //         module.userData?.moduleData?.depth + this.countertopOverhang;
  //     } else {
  //       countertop.userData.countertopWidth =
  //         countertopMeshes[0] === mesh ? module.userData?.moduleData?.width : 0;
  //     }

  //     // countertop.userData.countertopDepth =
  //     //   module.userData?.moduleData?.depth + this.countertopOverhang;

  //     countertopGroup.add(countertop);
  //   });

  //   countertopGroup.position.set(0, 0, 0);
  //   countertopGroup.rotation.set(0, 0, 0);
  //   countertopGroup.scale.set(1, 1, 1);

  //   this.sceneManager.scene.add(countertopGroup);

  //   return countertopGroup;
  // }

  addCountertopFromMeshTemplate(module) {
    const countertopMeshes = [];
    module.traverse(child => {
      if (
        child.isMesh &&
        child.userData.name &&
        child.userData.name.toUpperCase().includes('MAT-DA')
      ) {
        if (child.scale.x === 0 || child.scale.y === 0 || child.scale.z === 0) {
          return;
        }
        countertopMeshes.push(child);
      }
    });

    if (countertopMeshes.length === 0) {
      console.warn("Không tìm thấy mesh nào có tên chứa 'MAT-DA'");
      return null;
    }

    const countertopGroup = new THREE.Group();
    countertopGroup.name = 'SINGLE_COUNTERTOP';
    countertopGroup.userData.name = 'SINGLE_COUNTERTOP';

    countertopMeshes.forEach(mesh => {
      mesh.updateMatrixWorld(true);

      const worldPos = new THREE.Vector3();
      const worldQuat = new THREE.Quaternion();
      const worldScale = new THREE.Vector3();
      mesh.matrixWorld.decompose(worldPos, worldQuat, worldScale);

      const clonedGeometry = mesh.geometry.clone();
      clonedGeometry.computeBoundingBox();
      const bb = clonedGeometry.boundingBox;

      const geometryHeight = bb.max.y - bb.min.y;
      const newScaleY =
        geometryHeight > 0 ? this.countertopThickness / geometryHeight : 1;

      const realSizeX = (bb.max.x - bb.min.x) * worldScale.x;
      const realSizeZ = (bb.max.z - bb.min.z) * worldScale.z;
      this._recomputeUV(clonedGeometry, bb, realSizeX, realSizeZ);

      const plateMaterial = new THREE.MeshStandardMaterial({
        color: this.countertopColor,
        opacity: 1,
        transparent: false,
        side: THREE.FrontSide,
        roughness: this.countertopRoughness,
        metalness: this.countertopMetalness,
        flatShading: false,
        polygonOffset: true,
        polygonOffsetFactor: -1,
        polygonOffsetUnits: -1,
      });

      if (mesh.material?.map) {
        plateMaterial.map = mesh.material.map;
        plateMaterial.map.needsUpdate = true;
      }

      let countertop = new THREE.Mesh(clonedGeometry, plateMaterial);
      countertop.castShadow = true;
      countertop.receiveShadow = true;
      countertop.position.copy(worldPos);
      countertop.position.y -= this.countertopThickness / 2;
      countertop.quaternion.copy(worldQuat);
      countertop.scale.set(worldScale.x, newScaleY, worldScale.z);

      countertop.userData = {
        ...countertop.userData,
        ...(this.sceneManager.countertop.texture ?? {}),
        type: ModuleType.COUNTERTOP,
        requireCollision: true,
      };

      const isCorner = module.userData.moduleType?.includes('corner');
      const isCurvedBlock =
        module.userData.moduleType?.includes('curved_block');
      if (isCorner) {
        countertop.userData.name = mesh.userData.name;
        countertop.userData.countertopDepth =
          module.userData?.moduleData?.depth + this.countertopOverhang;
      } else {
        countertop.userData.countertopWidth =
          countertopMeshes[0] === mesh ? module.userData?.moduleData?.width : 0; //Lưu chiều rộng MB để tính giá

        countertop.userData.isCurvedCountertop =
          isCurvedBlock && countertopMeshes[0] === mesh; //Đánh dấu là MB bo cong để tính giá (chỉ đánh dấu 1 mesh nếu có nhiều mesh)
      }

      // ── CSG: đục lỗ sink ──────────────────────────────────────
      const sinkModels = this.findAllSinkModels([module]);

      if (sinkModels.length > 0) {
        const orientation = GeometryUtils.getModuleOrientation(module);
        let currentPlate = countertop.clone();

        for (let i = 0; i < sinkModels.length; i++) {
          const sinkModel = sinkModels[i];
          const sinkMesh = this.createHoleBox(sinkModel);

          const sinkWorldPosition = new THREE.Vector3();
          sinkModel.getWorldPosition(sinkWorldPosition);

          const sinkBox = new THREE.Box3().setFromObject(sinkModel);
          const sinkSize = new THREE.Vector3();
          sinkBox.getSize(sinkSize);

          const sinkPos = sinkModel.position.clone();

          const plateForCSG = currentPlate.clone();
          plateForCSG.position.set(0, 0, 0);
          plateForCSG.updateMatrixWorld(true);

          const sinkForCSG = sinkMesh.clone();

          let subOverhang;
          if (orientation === 'NORMAL') {
            subOverhang = {
              x: sinkSize.x / 2,
              y: 0,
              z: -sinkSize.z / 2,
            };
          } else if (orientation === 'ROTATED_90') {
            subOverhang = {
              x: -sinkSize.x / 2,
              y: 0,
              z: -sinkSize.z / 2,
            };
          } else if (orientation === 'ROTATED_180') {
            subOverhang = {
              x: -sinkSize.x / 2,
              y: 0,
              z: sinkSize.z / 2,
            };
          } else if (orientation === 'ROTATED_270') {
            subOverhang = {
              x: sinkSize.x / 2,
              y: 0,
              z: sinkSize.z / 2,
            };
          }

          // Tính position của mesh trong world space
          const meshWorldPos = new THREE.Vector3();
          // countertop.getWorldPosition(meshWorldPos);
          mesh.getWorldPosition(meshWorldPos);

          if (sinkModel.rotation.y !== 0) {
            //Nằm ở khối trái
            sinkForCSG.position.set(
              sinkWorldPosition.x - meshWorldPos.x + subOverhang.x,
              0,
              sinkWorldPosition.z - meshWorldPos.z - subOverhang.z // trừ thay vì cộng
            );
          } else {
            //Nằm ở khối phải
            sinkForCSG.position.set(
              sinkWorldPosition.x - meshWorldPos.x + subOverhang.x,
              0,
              sinkWorldPosition.z - meshWorldPos.z + subOverhang.z
            );
          }
          sinkForCSG.updateMatrixWorld(true);

          try {
            const plateWithHole = CSG.subtract(plateForCSG, sinkForCSG);

            if (
              plateWithHole?.geometry?.attributes?.position &&
              plateWithHole.geometry.attributes.position.count > 0
            ) {
              plateWithHole.material = plateMaterial.clone();
              plateWithHole.position.copy(countertop.position);
              plateWithHole.userData = { ...countertop.userData };
              currentPlate = plateWithHole;
            } else {
              break;
            }
          } catch (error) {
            break;
          }
        }

        currentPlate.geometry.computeBoundingBox();
        const finalBB = currentPlate.geometry.boundingBox;
        this._recomputeUV(currentPlate.geometry, finalBB);

        countertopGroup.add(currentPlate);
      } else {
        countertopGroup.add(countertop);
      }
    });

    countertopGroup.position.set(0, 0, 0);
    countertopGroup.rotation.set(0, 0, 0);
    countertopGroup.scale.set(1, 1, 1);

    this.sceneManager.scene.add(countertopGroup);

    return countertopGroup;
  }

  addBacksplashFromMeshTemplate(module) {
    const currentCabinet = this.sceneManager.cabinetManager.getCurrentCabinet();

    const foundModule = currentCabinet.getModuleByGroupUuid(module.uuid);
    const checkOpVachSau =
      foundModule.formularController.currentOptions?.opVachSau;
    const checkOpVachTrai =
      foundModule.formularController.currentOptions?.opVachTrai;
    const checkOpVachPhai =
      foundModule.formularController.currentOptions?.opVachPhai;

    if (!checkOpVachSau && !checkOpVachTrai && !checkOpVachPhai) return null;

    // Lưu rotation gốc
    const originalRotation = module.rotation.clone();

    // Reset rotation tạm thời để tính size chính xác
    module.rotation.set(0, 0, 0);
    module.updateMatrixWorld(true);

    const box = new THREE.Box3().setFromObject(
      GeometryUtils.moduleOnlyFrame(module)
    );
    const size = new THREE.Vector3();
    box.getSize(size);

    // Tìm tất cả mesh có tên chứa "VACH-BEP"
    const backsplashMeshes = [];
    module.traverse(child => {
      if (
        child.isMesh &&
        child.userData.name &&
        child.userData.name.toUpperCase().includes('VACH-BEP')
      ) {
        if (child.scale.x === 0 || child.scale.y === 0 || child.scale.z === 0) {
          return;
        }

        if (
          (module?.userData?.moduleData?.moduleType?.includes('corner') &&
            checkOpVachTrai &&
            child.userData.name.toUpperCase().includes('1')) ||
          (checkOpVachPhai && child.userData.name.toUpperCase().includes('2'))
        ) {
          backsplashMeshes.push(child.clone());
        } else if (checkOpVachSau) {
          backsplashMeshes.push(child.clone());
        }
      }
    });

    if (backsplashMeshes.length === 0) {
      console.warn("Không tìm thấy mesh nào có tên chứa 'VACH-BEP'");
      return null;
    }

    // Tạo group để chứa các mặt bếp
    const backsplashGroup = new THREE.Group();
    backsplashGroup.name = 'SINGLE_BACKSPLASH';
    backsplashGroup.userData.name = 'SINGLE_BACKSPLASH';

    // Material cho vách bếp

    // Clone từng mesh "VACH-BEP" để tạo 2 vách bếp
    backsplashMeshes.forEach((mesh, index) => {
      // Lấy thông tin từ mesh gốc
      const meshBox = new THREE.Box3().setFromObject(mesh);
      const meshSize = new THREE.Vector3();
      meshBox.getSize(meshSize);

      const meshCenter = new THREE.Vector3();
      meshBox.getCenter(meshCenter);

      // Tạo BoxGeometry - (width, height, depth)
      const geometry = new THREE.BoxGeometry(
        meshSize.x,
        this.backsplashHeight,
        meshSize.z
      );

      // Tạo material
      const backsplashMaterial = new THREE.MeshStandardMaterial({
        color: this.backsplashColor,
        opacity: this.backsplashOpacity,
        transparent: false,
        side: THREE.FrontSide,
        roughness: this.backsplashRoughness,
        metalness: this.backsplashMetalness,
        flatShading: false,
        // Thêm properties để giảm artifacts
        polygonOffset: true,
        polygonOffsetFactor: -1,
        polygonOffsetUnits: -1,
      });

      // Tạo mesh từ geometry và material
      const backsplash = new THREE.Mesh(geometry, backsplashMaterial);
      backsplash.castShadow = true; // đổ bóng
      backsplash.receiveShadow = true; // nhận bóng

      // Set position
      backsplash.position.copy(mesh.position);
      backsplash.position.setY(
        backsplash.position.y + this.backsplashHeight / 2
      );

      backsplash.position.setX(
        backsplash.position.x - size.x / 2 + meshSize.x / 2
      );
      backsplash.position.setZ(
        backsplash.position.z + size.z / 2 - meshSize.z / 2
      );

      // Thay đổi màu sắc để phân biệt (tùy chọn)
      if (backsplash.material) {
        backsplash.material = backsplashMaterial;
      }

      backsplash.userData = {
        ...backsplash.userData,
        ...(this.sceneManager.backsplash.texture ?? {}),
      };
      backsplash.userData.requireCollision = true;
      backsplash.userData.type = ModuleType.BACKSPLASH;

      // Thêm vào group
      backsplashGroup.add(backsplash);
    });

    // Restore rotation của moduleGroup
    module.rotation.copy(originalRotation);
    module.updateMatrixWorld(true);

    backsplashGroup.position.copy(module.position);
    backsplashGroup.rotation.copy(module.rotation);

    this.sceneManager.scene.add(backsplashGroup);

    return backsplashGroup;
  }

  _recomputeUV(geometry, bb) {
    const pos = geometry.attributes.position;
    const normal = geometry.attributes.normal;
    const uvArray = new Float32Array(pos.count * 2);

    const sizeX = bb.max.x - bb.min.x;
    const sizeY = bb.max.y - bb.min.y;
    const sizeZ = bb.max.z - bb.min.z;

    const cornerX = bb.max.x;
    const cornerZ = bb.min.z;

    for (let i = 0; i < pos.count; i++) {
      const x = pos.getX(i);
      const y = pos.getY(i);
      const z = pos.getZ(i);

      const nx = normal ? normal.getX(i) : 0;
      const ny = normal ? normal.getY(i) : 1;
      const nz = normal ? normal.getZ(i) : 0;

      const absNx = Math.abs(nx);
      const absNy = Math.abs(ny);
      const absNz = Math.abs(nz);

      let u, v;

      const isCurved = absNx > 0.1 && absNz > 0.1 && absNy < 0.8;

      if (isCurved) {
        const dx = x - cornerX;
        const dz = z - cornerZ;
        const angle = Math.atan2(-dz, -dx);
        const arcLength = Math.abs(angle) * Math.sqrt(dx * dx + dz * dz);
        u = arcLength / ((Math.PI / 2) * Math.min(sizeX, sizeZ) * 0.5);
        v = sizeY > 0 ? (y - bb.min.y) / sizeY : 0;
      } else if (absNy >= absNx && absNy >= absNz) {
        // Mặt TOP/BOTTOM: luôn theo X
        u = sizeX > 0 ? (x - bb.min.x) / sizeX : 0;
        v = sizeZ > 0 ? (z - bb.min.z) / sizeZ : 0;
      } else if (absNx >= absNy && absNx >= absNz) {
        // Mặt LEFT/RIGHT: luôn theo X → normal X nên dùng Z
        u = sizeZ > 0 ? (z - bb.min.z) / sizeZ : 0;
        v = sizeY > 0 ? (y - bb.min.y) / sizeY : 0;
      } else {
        // Mặt FRONT/BACK: luôn theo X
        u = sizeX > 0 ? (x - bb.min.x) / sizeX : 0;
        v = sizeY > 0 ? (y - bb.min.y) / sizeY : 0;
      }

      uvArray[i * 2] = u;
      uvArray[i * 2 + 1] = v;

      // // Quay texture 180 độ
      // uvArray[i * 2] = 1 - u;
      // uvArray[i * 2 + 1] = 1 - v;
    }

    geometry.setAttribute('uv', new THREE.BufferAttribute(uvArray, 2));
    geometry.attributes.uv.needsUpdate = true;
  }

  // Tạo mesh mặt bếp cho nhóm modules
  createCounterTopForGroup(groupId, moduleGroup) {
    // Tạo biến mới loại bỏ các phụ kiện khỏi từng group
    const moduleGroupWithoutAccessory =
      this.removeAccessoriesFromModelGroup(moduleGroup);

    //Thêm mặt bếp cho module góc
    if (
      (moduleGroup?.length === 1 &&
        moduleGroup[0]?.userData?.moduleData?.moduleType?.includes('corner')) ||
      moduleGroup[0]?.userData?.moduleData?.moduleType?.includes('column') ||
      moduleGroup[0]?.userData?.moduleData?.moduleType?.includes('curved_block')
    ) {
      const cornerCountertop = this.addCountertopFromMeshTemplate(
        moduleGroup[0]
      );

      this.groupPlanes.set(groupId, {
        plane: cornerCountertop,
        modules: moduleGroup,
      });

      // moduleGroup.forEach(module => {
      //   this.moduleGroups.set(module.uuid, groupId);
      // });

      return cornerCountertop;
    }

    const combinedBBox = this.calculateGroupBoundingBox(
      moduleGroupWithoutAccessory
    );

    const size = new THREE.Vector3();
    const center = new THREE.Vector3();

    combinedBBox.getSize(size);
    combinedBBox.getCenter(center);

    const plateMaterial = new THREE.MeshStandardMaterial({
      color: this.countertopColor,
      opacity: 1,
      transparent: false,
      side: THREE.FrontSide,
      roughness: this.countertopRoughness,
      metalness: this.countertopMetalness,
      flatShading: false,
      // Thêm properties để giảm artifacts
      polygonOffset: true,
      polygonOffsetFactor: -1,
      polygonOffsetUnits: -1,
    });

    // Xác định hướng chính của group dựa trên module đầu tiên trong group
    const orientation = GeometryUtils.getModuleOrientation(moduleGroup[0]);
    const isKichenIsLand =
      moduleGroup[0]?.userData?.moduleData?.moduleType?.includes(
        'kitchen_island'
      );

    const currentCabinet = this.sceneManager.cabinetManager.getCurrentCabinet();
    const foundModule = currentCabinet.getModuleByGroupUuid(
      moduleGroup[0]?.uuid
    );

    const options = foundModule?.formularController?.currentOptions ?? {};
    const cornerRadius = [
      options.rounded1 ?? 0,
      options.rounded2 ?? 0,
      options.rounded3 ?? 0,
      options.rounded4 ?? 0,
    ];
    const countertopExtensions = [
      options.extend1 ?? 0,
      options.extend2 ?? 0,
      options.extend3 ?? 0,
      options.extend4 ?? 0,
    ];

    const { geometry, position } = this.createCountertopGeometryAndPosition(
      orientation,
      size,
      center,
      combinedBBox,
      this.countertopThickness,
      isKichenIsLand,
      [1, 2, 3, 4], // 4 góc
      cornerRadius,
      countertopExtensions
    );

    const groupPlane = new THREE.Mesh(geometry, plateMaterial);
    groupPlane.castShadow = true; // đổ bóng
    groupPlane.receiveShadow = true; // nhận bóng
    groupPlane.position.copy(position);

    groupPlane.name = 'GROUP_COUNTERTOP';
    groupPlane.userData = {
      type: ModuleType.COUNTERTOP,
      requireCollision: true,
      groupId: groupId,
      moduleIds: moduleGroup.map(m => m.uuid),
      isGroupPlane: true,
      orientation: orientation, //góc quay để tính giá chính xác cho những tủ kh phải là tủ góc
      ...(this.sceneManager.countertop.texture ?? {}),
    };

    // Tìm tất cả model SINK
    const sinkModels = this.findAllSinkModels(moduleGroup);

    if (sinkModels.length > 0) {
      // Bắt đầu với groupPlane gốc
      let currentPlate = groupPlane.clone();
      const sinkMeshes = []; // Để debug

      // Đục lỗ cho từng sink
      for (let i = 0; i < sinkModels.length; i++) {
        const sinkModel = sinkModels[i];
        const sinkMesh = this.createHoleBox(sinkModel);

        // Lấy world position của sink model
        const sinkWorldPosition = new THREE.Vector3();
        sinkModel.getWorldPosition(sinkWorldPosition);

        // Đặt sinkMesh ở đúng vị trí của sink model (cho debug visualization)
        sinkMesh.position.set(
          sinkWorldPosition.x,
          groupPlane.position.y, // Cùng mức Y với groupPlane
          sinkWorldPosition.z
        );

        sinkMeshes.push(sinkMesh);

        // Lấy size của sink model
        const sinkBox = new THREE.Box3().setFromObject(sinkModel);
        const sinkSize = new THREE.Vector3();
        sinkBox.getSize(sinkSize);

        // Tạo bản sao cho CSG operation (CSG cần position = 0,0,0)
        const plateForCSG = currentPlate.clone();
        plateForCSG.position.set(0, 0, 0);
        plateForCSG.updateMatrixWorld(true);

        const sinkForCSG = sinkMesh.clone();
        // Tính vị trí tương đối của sink so với center của plate

        let subOverhang;
        if (orientation === 'NORMAL') {
          subOverhang = {
            x: (sinkSize.x - this.countertopOverhang) / 2,
            y: 0,
            z: -sinkSize.z / 2,
          };
        } else if (orientation === 'ROTATED_90') {
          subOverhang = {
            x: -sinkSize.x / 2,
            y: 0,
            z: (-sinkSize.z + this.countertopOverhang) / 2,
          };
        } else if (orientation === 'ROTATED_180') {
          subOverhang = {
            x: (-sinkSize.x + this.countertopOverhang) / 2,
            y: 0,
            z: sinkSize.z / 2,
          };
        } else if (orientation === 'ROTATED_270') {
          subOverhang = {
            x: sinkSize.x / 2,
            y: 0,
            z: (sinkSize.z - this.countertopOverhang) / 2,
          };
        }

        sinkForCSG.position.set(
          sinkWorldPosition.x - center.x + subOverhang.x,
          0, // Y = 0 cho CSG
          sinkWorldPosition.z - center.z + subOverhang.z
        );

        sinkForCSG.updateMatrixWorld(true);

        // Thực hiện CSG subtract
        try {
          const plateWithHole = CSG.subtract(plateForCSG, sinkForCSG);

          if (
            plateWithHole &&
            plateWithHole.geometry &&
            plateWithHole.geometry.attributes.position
          ) {
            const vertexCount =
              plateWithHole.geometry.attributes.position.count;

            if (vertexCount > 0) {
              // Nếu không phải lần đầu, remove plate cũ khỏi scene
              if (i > 0 && currentPlate !== groupPlane) {
                this.sceneManager.scene.remove(currentPlate);
              }

              // Material cải thiện cho bề mặt mịn
              plateWithHole.material = new THREE.MeshStandardMaterial({
                color: this.countertopColor,
                opacity: 1,
                transparent: false,
                side: THREE.FrontSide,
                roughness: this.countertopRoughness,
                metalness: this.countertopMetalness,
                flatShading: false,
                // Thêm properties để giảm artifacts
                polygonOffset: true,
                polygonOffsetFactor: -1,
                polygonOffsetUnits: -1,
              });

              // Khôi phục position gốc cho plate với lỗ
              plateWithHole.position.copy(groupPlane.position);
              plateWithHole.userData = { ...groupPlane.userData };

              // Cập nhật currentPlate cho lần subtract tiếp theo
              currentPlate = plateWithHole;
            } else {
              break;
            }
          } else {
            break;
          }
        } catch (error) {
          break;
        }
      }

      currentPlate.geometry.computeBoundingBox();
      const bb = currentPlate.geometry.boundingBox;
      this._recomputeUV(currentPlate.geometry, bb);

      // Add plate cuối cùng (đã có tất cả lỗ) vào scene
      this.sceneManager.scene.add(currentPlate);

      this.groupPlanes.set(groupId, {
        plane: currentPlate,
        modules: moduleGroup,
        boundingBox: combinedBBox.clone(),
        sinkCount: sinkModels.length,
      });

      return currentPlate;
    } else {
      groupPlane.geometry.computeBoundingBox();
      const bb = groupPlane.geometry.boundingBox;
      this._recomputeUV(groupPlane.geometry, bb);

      this.sceneManager.scene.add(groupPlane);

      this.groupPlanes.set(groupId, {
        plane: groupPlane,
        modules: moduleGroup,
        boundingBox: combinedBBox.clone(),
        sinkCount: 0,
      });

      return groupPlane;
    }
  }

  // // Tạo geometry và position cho mặt bếp dựa trên orientation
  // createCountertopGeometryAndPosition(
  //   orientation,
  //   size,
  //   center,
  //   combinedBBox,
  //   countertopThickness,
  //   isKichenIsLand
  // ) {
  //   let geometry, position;

  //   const kIslandOverhang = isKichenIsLand ? 2 * this.countertopOverhang : 0;

  //   if (orientation === 'NORMAL') {
  //     geometry = new THREE.BoxGeometry(
  //       size.x + this.countertopOverhang + kIslandOverhang / 2,
  //       countertopThickness,
  //       size.z + kIslandOverhang
  //     );

  //     position = new THREE.Vector3(
  //       center.x + this.countertopOverhang / 2 - kIslandOverhang / 4,
  //       combinedBBox.max.y,
  //       center.z
  //     );
  //   } else if (orientation === 'ROTATED_90') {
  //     geometry = new THREE.BoxGeometry(
  //       size.x + kIslandOverhang,
  //       countertopThickness,
  //       size.z + this.countertopOverhang + kIslandOverhang / 2
  //     );

  //     position = new THREE.Vector3(
  //       center.x,
  //       combinedBBox.max.y,
  //       center.z - this.countertopOverhang / 2 + kIslandOverhang / 4
  //     );
  //   } else if (orientation === 'ROTATED_180') {
  //     geometry = new THREE.BoxGeometry(
  //       size.x + this.countertopOverhang + kIslandOverhang / 2,
  //       countertopThickness,
  //       size.z + kIslandOverhang
  //     );

  //     position = new THREE.Vector3(
  //       center.x - this.countertopOverhang / 2 + kIslandOverhang / 4,
  //       combinedBBox.max.y,
  //       center.z
  //     );
  //   } else if (orientation === 'ROTATED_270') {
  //     geometry = new THREE.BoxGeometry(
  //       size.x + kIslandOverhang,
  //       countertopThickness,
  //       size.z + this.countertopOverhang + kIslandOverhang / 2
  //     );

  //     position = new THREE.Vector3(
  //       center.x,
  //       combinedBBox.max.y,
  //       center.z + this.countertopOverhang / 2 - kIslandOverhang / 4
  //     );
  //   }

  //   return { geometry, position };
  // }

  createCountertopGeometryAndPosition(
    orientation,
    size,
    center,
    combinedBBox,
    countertopThickness,
    isKichenIsLand,
    roundedCorners = [1, 2, 3, 4],
    cornerRadius ,
    countertopExtensions
  ) {
    let geometry, position;

    const kIslandOverhang = isKichenIsLand ? 2 * this.countertopOverhang : 0;

    const extendSizeD = isKichenIsLand
      ? countertopExtensions[0] + countertopExtensions[2]
      : 0;
    const extendSizeW = isKichenIsLand
      ? countertopExtensions[1] + countertopExtensions[3]
      : 0;
    const extendPosD = isKichenIsLand
      ? countertopExtensions[0] - countertopExtensions[2]
      : 0;
    const extendPosW = isKichenIsLand
      ? countertopExtensions[1] - countertopExtensions[3]
      : 0;

    const cornerIndexMap = {
      NORMAL: [3, 0, 1, 2],
      ROTATED_90: [2, 3, 0, 1],
      ROTATED_180: [1, 2, 3, 0],
      ROTATED_270: [0, 1, 2, 3],
    };

    // Lấy bán kính bo cho 1 góc vật lý cụ thể (1-4), mặc định 0 nếu không truyền
    const getRadiusForCorner = cornerNum => {
      return cornerRadius?.[cornerNum - 1] ?? 0;
    };

    const createGeometry = (width, thickness, depth, indexMap) => {
      // if (!roundedCorners || roundedCorners.length === 0) {
      if (!isKichenIsLand) {
        return new THREE.BoxGeometry(width, thickness, depth);
      }

      // 4 góc cục bộ theo chiều kim đồng hồ trên mặt phẳng X-Z
      const corners = [
        { x: 0, z: 0 }, //1
        { x: 0, z: depth }, //2
        { x: width, z: depth }, //3
        { x: width, z: 0 }, //4
      ];

      // map: local index -> bán kính bo (đã clamp theo width/depth), 0 nếu không bo
      const radiusByLocalIndex = new Array(4).fill(0);
      roundedCorners.forEach(cornerNum => {
        const localIndex = indexMap[cornerNum - 1];
        const rawR = getRadiusForCorner(cornerNum);
        const clampedR = Math.min(rawR, width / 2, depth / 2);
        radiusByLocalIndex[localIndex] = clampedR;
      });

      const isRounded = i => radiusByLocalIndex[i] > 0;

      const getInPoint = (i, r) => {
        const cur = corners[i];
        const prev = corners[(i + 3) % 4];
        const dx = cur.x - prev.x;
        const dz = cur.z - prev.z;
        const len = Math.hypot(dx, dz);
        return { x: cur.x - (dx / len) * r, z: cur.z - (dz / len) * r };
      };

      const getOutPoint = (i, r) => {
        const cur = corners[i];
        const next = corners[(i + 1) % 4];
        const dx = next.x - cur.x;
        const dz = next.z - cur.z;
        const len = Math.hypot(dx, dz);
        return { x: cur.x + (dx / len) * r, z: cur.z + (dz / len) * r };
      };

      const shape = new THREE.Shape();
      const startPoint = isRounded(0)
        ? getInPoint(0, radiusByLocalIndex[0])
        : corners[0];
      shape.moveTo(startPoint.x, startPoint.z);

      for (let i = 0; i < 4; i++) {
        if (isRounded(i)) {
          const r = radiusByLocalIndex[i];
          const inPoint = getInPoint(i, r);
          const outPoint = getOutPoint(i, r);
          shape.lineTo(inPoint.x, inPoint.z);
          shape.quadraticCurveTo(
            corners[i].x,
            corners[i].z,
            outPoint.x,
            outPoint.z
          );
        } else {
          shape.lineTo(corners[i].x, corners[i].z);
        }
      }
      shape.closePath();

      const geo = new THREE.ExtrudeGeometry(shape, {
        depth: thickness,
        bevelEnabled: false,
      });

      geo.rotateX(-Math.PI / 2);
      geo.translate(-width / 2, -thickness / 2, depth / 2);

      return geo;
    };

    const indexMap = cornerIndexMap[orientation];

    if (orientation === 'NORMAL') {
      geometry = createGeometry(
        size.x + this.countertopOverhang + kIslandOverhang / 2 + extendSizeD,
        countertopThickness,
        size.z + kIslandOverhang + extendSizeW,
        indexMap
      );

      position = new THREE.Vector3(
        center.x +
          this.countertopOverhang / 2 -
          kIslandOverhang / 4 +
          extendPosD / 2,
        combinedBBox.max.y,
        center.z + extendPosW / 2
      );
    } else if (orientation === 'ROTATED_90') {
      geometry = createGeometry(
        size.x + kIslandOverhang + extendSizeW,
        countertopThickness,
        size.z + this.countertopOverhang + kIslandOverhang / 2 + extendSizeD,
        indexMap
      );

      position = new THREE.Vector3(
        center.x + extendPosW / 2,
        combinedBBox.max.y,
        center.z -
          this.countertopOverhang / 2 +
          kIslandOverhang / 4 -
          extendPosD / 2
      );
    } else if (orientation === 'ROTATED_180') {
      geometry = createGeometry(
        size.x + this.countertopOverhang + kIslandOverhang / 2 + extendSizeD,
        countertopThickness,
        size.z + kIslandOverhang + extendSizeW,
        indexMap
      );

      position = new THREE.Vector3(
        center.x -
          this.countertopOverhang / 2 +
          kIslandOverhang / 4 -
          extendPosD / 2,
        combinedBBox.max.y,
        center.z - extendPosW / 2
      );
    } else if (orientation === 'ROTATED_270') {
      geometry = createGeometry(
        size.x + kIslandOverhang + extendSizeW,
        countertopThickness,
        size.z + this.countertopOverhang + kIslandOverhang / 2 + extendSizeD,
        indexMap
      );

      position = new THREE.Vector3(
        center.x - extendPosW / 2,
        combinedBBox.max.y,
        center.z +
          this.countertopOverhang / 2 -
          kIslandOverhang / 4 +
          extendPosD / 2
      );
    }

    return { geometry, position };
  }

  // Tạo vách bếp cho nhóm modules với logic chung cho mọi góc quay
  createBacksplashForGroup(groupId, moduleGroup) {
    //Bỏ qua đảo bếp
    if (
      moduleGroup[0]?.userData?.moduleData?.moduleType?.includes(
        'kitchen_island'
      )
    ) {
      return;
    }

    // Tạo biến mới loại bỏ các phụ kiện khỏi từng group
    const moduleGroupWithoutAccessory =
      this.removeAccessoriesFromModelGroup(moduleGroup);

    //Thêm mặt bếp cho module góc
    if (
      (moduleGroup?.length === 1 &&
        moduleGroup[0]?.userData?.moduleData?.moduleType?.includes('corner')) ||
      moduleGroup[0]?.userData?.moduleData?.moduleType?.includes('column')
    ) {
      const cornerBacksplash = this.addBacksplashFromMeshTemplate(
        moduleGroup[0]
      );

      this.groupPlanes.set(groupId, {
        plane: cornerBacksplash,
        modules: moduleGroup,
      });

      return cornerBacksplash;
    }

    const combinedBBox = this.calculateGroupBoundingBox(
      moduleGroupWithoutAccessory
    );
    const size = new THREE.Vector3();
    const center = new THREE.Vector3();

    combinedBBox.getSize(size);
    combinedBBox.getCenter(center);

    // Xác định hướng chính của group dựa trên module đầu tiên trong group
    const orientation = GeometryUtils.getModuleOrientation(moduleGroup[0]);

    // Thông số vách bếp
    const backsplashHeight = this.backsplashHeight;
    const backsplashThickness = 0.005; // Độ dày vách bếp (0.5cm)

    // Tạo geometry và position dựa trên orientation
    const { geometry, position } = this.createBacksplashGeometryAndPosition(
      orientation,
      size,
      center,
      combinedBBox,
      backsplashHeight,
      backsplashThickness
    );

    // Material cho vách bếp
    const backsplashMaterial = new THREE.MeshStandardMaterial({
      color: this.backsplashColor,
      opacity: this.backsplashOpacity,
      transparent: false,
      side: THREE.DoubleSide,
      roughness: this.backsplashRoughness,
      metalness: this.backsplashMetalness,
      flatShading: false,
      // Thêm properties để giảm artifacts
      polygonOffset: true,
      polygonOffsetFactor: -1,
      polygonOffsetUnits: -1,
    });

    const groupBacksplash = new THREE.Mesh(geometry, backsplashMaterial);
    groupBacksplash.castShadow = true; // đổ bóng
    groupBacksplash.receiveShadow = true; // nhận bóng
    groupBacksplash.position.copy(position);

    groupBacksplash.name = 'GROUP_BACKSPLASH';
    groupBacksplash.userData = {
      type: ModuleType.BACKSPLASH,
      requireCollision: true,
      groupId: groupId,
      moduleIds: moduleGroup.map(m => m.uuid),
      isGroupBacksplash: true,
      name: 'SINGLE_BACKSPLASH',
      orientation: orientation,
      ...(this.sceneManager.backsplash.texture ?? {}),
    };

    // Add vách bếp vào scene
    this.sceneManager.scene.add(groupBacksplash);

    // Lưu thông tin vách bếp vào Map
    this.groupPlanes.set(groupId, {
      plane: groupBacksplash,
      modules: moduleGroup,
      boundingBox: combinedBBox.clone(),
      orientation: orientation,
    });

    return groupBacksplash;
  }

  // Tạo geometry và position cho vách bếp dựa trên orientation
  createBacksplashGeometryAndPosition(
    orientation,
    size,
    center,
    combinedBBox,
    backsplashHeight,
    backsplashThickness
  ) {
    let geometry, position;

    if (orientation === 'NORMAL') {
      geometry = new THREE.BoxGeometry(
        backsplashThickness,
        backsplashHeight,
        size.z
      );

      position = new THREE.Vector3(
        combinedBBox.min.x + backsplashThickness / 2,
        combinedBBox.max.y + backsplashHeight / 2 + this.countertopHeight / 2,
        center.z
      );
    } else if (orientation === 'ROTATED_90') {
      geometry = new THREE.BoxGeometry(
        size.x,
        backsplashHeight,
        backsplashThickness
      );

      position = new THREE.Vector3(
        center.x,
        combinedBBox.max.y + backsplashHeight / 2 + this.countertopHeight / 2,
        combinedBBox.max.z - backsplashThickness / 2
      );
    } else if (orientation === 'ROTATED_180') {
      geometry = new THREE.BoxGeometry(
        backsplashThickness,
        backsplashHeight,
        size.z
      );

      position = new THREE.Vector3(
        combinedBBox.max.x - backsplashThickness / 2,
        combinedBBox.max.y + backsplashHeight / 2 + this.countertopHeight / 2,
        center.z
      );
    } else if (orientation === 'ROTATED_270') {
      geometry = new THREE.BoxGeometry(
        size.x,
        backsplashHeight,
        backsplashThickness
      );

      position = new THREE.Vector3(
        center.x,
        combinedBBox.max.y + backsplashHeight / 2 + this.countertopHeight / 2,
        combinedBBox.min.z + backsplashThickness / 2
      );
    }

    return { geometry, position };
  }

  // Tạo geometry và position cho vách bếp ốp bìa trên orientation
  // createEdgeBacksplashGeometryAndPosition(
  //   orientation,
  //   size,
  //   mdPosition,
  //   backsplashHeight,
  //   backsplashThickness
  // ) {
  //   let edgeGeometry, leftEdgePosition, rightEdgePosition;

  //   if (orientation === 'NORMAL') {
  //     edgeGeometry = new THREE.BoxGeometry(
  //       size.x + this.countertopOverhang,
  //       backsplashHeight,
  //       backsplashThickness
  //     );

  //     leftEdgePosition = new THREE.Vector3(
  //       mdPosition.x + this.countertopOverhang / 2,
  //       size.y + backsplashHeight / 2 + this.countertopHeight / 2,
  //       mdPosition.z + (size.z / 2 - backsplashThickness / 2)
  //     );

  //     rightEdgePosition = new THREE.Vector3(
  //       mdPosition.x + this.countertopOverhang / 2,
  //       size.y + backsplashHeight / 2 + this.countertopHeight / 2,
  //       mdPosition.z - (size.z / 2 - backsplashThickness / 2)
  //     );
  //   } else if (orientation === 'ROTATED_90') {
  //     edgeGeometry = new THREE.BoxGeometry(
  //       backsplashThickness,
  //       backsplashHeight,
  //       size.z + this.countertopOverhang
  //     );

  //     leftEdgePosition = new THREE.Vector3(
  //       mdPosition.x + (size.x / 2 - backsplashThickness / 2),
  //       size.y + backsplashHeight / 2 + this.countertopHeight / 2,
  //       mdPosition.z - this.countertopOverhang / 2
  //     );

  //     rightEdgePosition = new THREE.Vector3(
  //       mdPosition.x - (size.x / 2 - backsplashThickness / 2),
  //       size.y + backsplashHeight / 2 + this.countertopHeight / 2,
  //       mdPosition.z - this.countertopOverhang / 2
  //     );
  //   } else if (orientation === 'ROTATED_180') {
  //     edgeGeometry = new THREE.BoxGeometry(
  //       size.x + this.countertopOverhang,
  //       backsplashHeight,
  //       backsplashThickness
  //     );

  //     leftEdgePosition = new THREE.Vector3(
  //       mdPosition.x - this.countertopOverhang / 2,
  //       size.y + backsplashHeight / 2 + this.countertopHeight / 2,
  //       mdPosition.z - (size.z / 2 - backsplashThickness / 2)
  //     );

  //     rightEdgePosition = new THREE.Vector3(
  //       mdPosition.x - this.countertopOverhang / 2,
  //       size.y + backsplashHeight / 2 + this.countertopHeight / 2,
  //       mdPosition.z + (size.z / 2 - backsplashThickness / 2)
  //     );
  //   } else if (orientation === 'ROTATED_270') {
  //     edgeGeometry = new THREE.BoxGeometry(
  //       backsplashThickness,
  //       backsplashHeight,
  //       size.z + this.countertopOverhang
  //     );

  //     leftEdgePosition = new THREE.Vector3(
  //       mdPosition.x - (size.x / 2 - backsplashThickness / 2),
  //       size.y + backsplashHeight / 2 + this.countertopHeight / 2,
  //       mdPosition.z + this.countertopOverhang / 2
  //     );

  //     rightEdgePosition = new THREE.Vector3(
  //       mdPosition.x + (size.x / 2 - backsplashThickness / 2),
  //       size.y + backsplashHeight / 2 + this.countertopHeight / 2,
  //       mdPosition.z + this.countertopOverhang / 2
  //     );
  //   }

  //   return { edgeGeometry, leftEdgePosition, rightEdgePosition };
  // }
  createEdgeBacksplashGeometryAndPosition(
    orientation,
    size,
    mdPosition,
    backsplashHeight,
    backsplashThickness,
    hasRoundedCorner = false,
    cornerRadius = 0.05
  ) {
    let edgeGeometry, leftEdgePosition, rightEdgePosition;

    const createGeometry = (width, height, depth) => {
      if (!hasRoundedCorner) {
        return new THREE.BoxGeometry(width, height, depth);
      }

      const r = Math.min(cornerRadius, width / 2, height / 2);
      const shape = new THREE.Shape();

      shape.moveTo(0, 0);
      shape.lineTo(width, 0);
      shape.lineTo(width, height - r);
      shape.quadraticCurveTo(width, height, width - r, height);
      shape.lineTo(0, height);
      shape.lineTo(0, 0);

      const geo = new THREE.ExtrudeGeometry(shape, {
        depth,
        bevelEnabled: false,
      });

      // Căn giữa giống BoxGeometry
      geo.translate(-width / 2, -height / 2, -depth / 2);

      return geo;
    };

    // Dùng chung công thức Y cho cả 2 trường hợp
    const getYPosition = () => {
      return size.y + backsplashHeight / 2 + this.countertopHeight / 2;
    };

    if (orientation === 'NORMAL') {
      edgeGeometry = createGeometry(
        size.x + this.countertopOverhang,
        backsplashHeight,
        backsplashThickness
      );

      leftEdgePosition = new THREE.Vector3(
        mdPosition.x + this.countertopOverhang / 2,
        getYPosition(),
        mdPosition.z + (size.z / 2 - backsplashThickness / 2)
      );

      rightEdgePosition = new THREE.Vector3(
        mdPosition.x + this.countertopOverhang / 2,
        getYPosition(),
        mdPosition.z - (size.z / 2 - backsplashThickness / 2)
      );
    } else if (orientation === 'ROTATED_90') {
      edgeGeometry = createGeometry(
        size.z + this.countertopOverhang,
        backsplashHeight,
        backsplashThickness
      );
      edgeGeometry.rotateY(Math.PI / 2);

      leftEdgePosition = new THREE.Vector3(
        mdPosition.x + (size.x / 2 - backsplashThickness / 2),
        getYPosition(),
        mdPosition.z - this.countertopOverhang / 2
      );

      rightEdgePosition = new THREE.Vector3(
        mdPosition.x - (size.x / 2 - backsplashThickness / 2),
        getYPosition(),
        mdPosition.z - this.countertopOverhang / 2
      );
    } else if (orientation === 'ROTATED_180') {
      edgeGeometry = createGeometry(
        size.x + this.countertopOverhang,
        backsplashHeight,
        backsplashThickness
      );
      edgeGeometry.rotateY(Math.PI);

      leftEdgePosition = new THREE.Vector3(
        mdPosition.x - this.countertopOverhang / 2,
        getYPosition(),
        mdPosition.z - (size.z / 2 - backsplashThickness / 2)
      );

      rightEdgePosition = new THREE.Vector3(
        mdPosition.x - this.countertopOverhang / 2,
        getYPosition(),
        mdPosition.z + (size.z / 2 - backsplashThickness / 2)
      );
    } else if (orientation === 'ROTATED_270') {
      edgeGeometry = createGeometry(
        size.z + this.countertopOverhang,
        backsplashHeight,
        backsplashThickness
      );
      edgeGeometry.rotateY(-Math.PI / 2);

      leftEdgePosition = new THREE.Vector3(
        mdPosition.x - (size.x / 2 - backsplashThickness / 2),
        getYPosition(),
        mdPosition.z + this.countertopOverhang / 2
      );

      rightEdgePosition = new THREE.Vector3(
        mdPosition.x + (size.x / 2 - backsplashThickness / 2),
        getYPosition(),
        mdPosition.z + this.countertopOverhang / 2
      );
    }

    return { edgeGeometry, leftEdgePosition, rightEdgePosition };
  }

  // Xóa group plane
  removeGroupPlane(groupId) {
    const groupData = this.groupPlanes.get(groupId);
    if (groupData) {
      const plane = groupData.plane;

      if (!plane) return;

      // Traverse tất cả mesh con để tắt raycast và dispose
      plane.traverse(child => {
        if (child.isMesh) {
          child.raycast = () => {};

          if (child.geometry) child.geometry.dispose();
          if (child.material) {
            if (Array.isArray(child.material)) {
              child.material.forEach(m => m.dispose());
            } else {
              child.material.dispose();
            }
          }
        }
      });

      // Xóa khỏi scene
      if (plane.parent) {
        plane.parent.remove(plane);
      } else {
        this.sceneManager.scene.remove(plane);
      }

      groupData.modules.forEach(module => {
        this.moduleGroups.delete(module.uuid);
      });

      this.groupPlanes.delete(groupId);
    }
  }

  // Tạo tất cả group planes với validation nghiêm ngặt
  async createAllGroupPlanes() {
    this.clearAllGroupPlanes();

    if (!this.sceneManager) return;

    if (
      this.sceneManager.countertop &&
      this.sceneManager.backsplash &&
      (this.sceneManager.countertop.using !== false ||
        this.sceneManager.backsplash.using !== false)
    ) {
      const allModules = this.getAllKitchenLowerModules();
      if (allModules.length === 0) {
        return [];
      }

      const moduleGroupsForCountertop =
        this.findModuleGroupsForCountertop(allModules);
      const moduleGroupsForBacksplash =
        this.findModuleGroupsForBacksplash(allModules);

      const createdPlanes = [];
      const currentCabinet =
        this.sceneManager.cabinetManager.getCurrentCabinet();

      // Tạo mặt bếp
      for (let i = 0; i < moduleGroupsForCountertop.length; i++) {
        const group = moduleGroupsForCountertop[i];

        // Validate group với điều kiện nghiêm ngặt
        if (this.validateGroup(group)) {
          const groupCountertopId = `countertopGroup_${i}_${Date.now()}`;

          try {
            //MB
            if (this.sceneManager.countertop.using === true) {
              const groupPlane = this.createCounterTopForGroup(
                groupCountertopId,
                group
              );
              if (groupPlane) {
                createdPlanes.push(groupPlane);
                const texture = this.sceneManager.countertop.texture;

                if (texture)
                  this.sceneManager.textureController.changeTextureCountertopAndBacksplash(
                    {
                      scene: this.sceneManager.scene,
                      textureType: 'map',
                      texture,
                      includeMeshes: ['MAT-DA', 'COUNTERTOP'],
                      includeTypes: [ModuleType.COUNTERTOP],
                    }
                  );

                //Thêm vào cabinetGroup để có thể click chọn trên 3d
                currentCabinet.cabinetGroup.push(groupPlane);
              }
            }
          } catch (error) {
            console.error(`Failed to create group`, error);
          }
        } else {
          console.log(`Group with ${group.length} modules failed validation`);
        }
      }

      // Tạo vách bếp
      for (let i = 0; i < moduleGroupsForBacksplash.length; i++) {
        const group = moduleGroupsForBacksplash[i];

        // Validate group với điều kiện nghiêm ngặt
        if (this.validateGroup(group)) {
          const groupBacksplashId = `backsplashGroup_${i}_${Date.now()}`;

          try {
            //VB
            if (this.sceneManager.backsplash.using === true) {
              const groupBacksplash = this.createBacksplashForGroup(
                groupBacksplashId,
                group
              );

              if (groupBacksplash) {
                createdPlanes.push(groupBacksplash);

                const texture = this.sceneManager.backsplash.texture;

                if (texture)
                  this.sceneManager.textureController.changeTextureCountertopAndBacksplash(
                    {
                      scene: this.sceneManager.scene,
                      textureType: 'map',
                      texture,
                      includeTypes: [ModuleType.BACKSPLASH],
                      includeMeshes: ['BACKSPLASH', 'VACH-BEP'],
                    }
                  );

                //Thêm vào cabinetGroup để có thể click chọn trên 3d
                currentCabinet.cabinetGroup.push(groupBacksplash);
              }
            }
          } catch (error) {
            console.error(`Failed to create group`, error);
          }
        } else {
          console.log(`Group with ${group.length} modules failed validation`);
        }
      }

      //Ốp vách cho những module được đánh dấu có option ốp vách
      for (let i = 0; i < allModules.length; i++) {
        const md = allModules[i];
        const groupEdgeBacksplashId = `groupEdgeBacksplash_${i}_${Date.now()}`;
        const foundModule = currentCabinet.getModuleByGroupUuid(md.uuid);
        const withLeftEdgeBacksplash =
          foundModule.formularController.currentOptions?.opVachBiaTrai;
        const withRightEdgeBacksplash =
          foundModule.formularController.currentOptions?.opVachBiaPhai;
        const hasRoundedCorner =
          foundModule.formularController.currentOptions?.opVachBiaBoCong;
        const cornerRadius =
          foundModule.formularController.currentOptions?.rBoOpVachBia || 0.1;

        if (
          this.sceneManager.backsplash.using === true &&
          (withLeftEdgeBacksplash || withRightEdgeBacksplash)
        ) {
          const mdBox = new THREE.Box3().setFromObject(
            GeometryUtils.moduleOnlyFrame(md)
          );
          const mdSize = new THREE.Vector3();
          mdBox.getSize(mdSize);

          //Tạo ốp vách bìa cho module có option thêm vách bìa
          const orientation = GeometryUtils.getModuleOrientation(md);
          const backsplashHeight = this.backsplashHeight;
          const backsplashThickness = 0.005; // Độ dày vách bếp (0.5cm)

          const backsplashMaterial = new THREE.MeshStandardMaterial({
            color: this.backsplashColor,
            opacity: this.backsplashOpacity,
            transparent: false,
            side: THREE.FrontSide,
            roughness: this.backsplashRoughness,
            metalness: this.backsplashMetalness,
            flatShading: false,
            // Thêm properties để giảm artifacts
            polygonOffset: true,
            polygonOffsetFactor: -1,
            polygonOffsetUnits: -1,
          });

          const { edgeGeometry, leftEdgePosition, rightEdgePosition } =
            this.createEdgeBacksplashGeometryAndPosition(
              orientation,
              mdSize,
              md.position,
              backsplashHeight,
              backsplashThickness,
              hasRoundedCorner,
              cornerRadius
            );

          const groupEdgeBacksplash = new THREE.Group();

          const baseEdgeBacksplash = new THREE.Mesh(
            edgeGeometry,
            backsplashMaterial
          );
          baseEdgeBacksplash.castShadow = true; // đổ bóng
          baseEdgeBacksplash.receiveShadow = true; // nhận bóng

          baseEdgeBacksplash.name = 'EDGE_BACKSPLASH';
          baseEdgeBacksplash.userData = {
            isCurvedEdgeBacksplash: hasRoundedCorner, // Đánh dấu nếu đây là vách bếp ốp bìa có bo cong để tính giá
            type: ModuleType.BACKSPLASH,
            requireCollision: true,
            groupId: groupEdgeBacksplashId,
            moduleIds: [md.uuid],
            isGroupBacksplash: true,
            isEdgeBacksplash: true,
            name: 'EDGE_BACKSPLASH',
            orientation: orientation,
            ...(this.sceneManager.backsplash.texture ?? {}),
          };

          const leftEdgeBacksplash = baseEdgeBacksplash.clone();
          const rightEdgeBacksplash = baseEdgeBacksplash.clone();
          leftEdgeBacksplash.position.copy(leftEdgePosition);
          rightEdgeBacksplash.position.copy(rightEdgePosition);

          if (withLeftEdgeBacksplash) {
            groupEdgeBacksplash.add(leftEdgeBacksplash);
          }
          if (withRightEdgeBacksplash) {
            groupEdgeBacksplash.add(rightEdgeBacksplash);
          }

          const texture = this.sceneManager.backsplash.texture;

          if (texture)
            this.sceneManager.textureController.changeTextureCountertopAndBacksplash(
              {
                scene: this.sceneManager.scene,
                textureType: 'map',
                texture,
                includeTypes: [ModuleType.BACKSPLASH],
                includeMeshes: ['BACKSPLASH', 'VACH-BEP'],
              }
            );

          //Thêm vào edgeBacksplash để có thể click chọn trên 3d
          currentCabinet.cabinetGroup.push(groupEdgeBacksplash);

          // Add ốp bìa vách bếp vào scene
          this.sceneManager.scene.add(groupEdgeBacksplash);

          // Lưu thông tin vách bếp vào Map
          this.groupPlanes.set(groupEdgeBacksplashId, {
            plane: groupEdgeBacksplash,
            modules: [md],
            boundingBox: mdBox.clone(),
            orientation: orientation,
          });

          createdPlanes.push(groupEdgeBacksplash);
        }
      }

      // return createdPlanes;
    }

    if (
      this.sceneManager.ledDecor &&
      this.sceneManager.ledDecor.using !== false
    ) {
      //Thêm lại đèn theo VB mới
      setTimeout(() => {
        this.sceneManager.addKitchenLeds();
      }, 200);
    }
  }

  // Xóa tất cả group planes
  clearAllGroupPlanes() {
    const groupIds = Array.from(this.groupPlanes.keys());
    groupIds.forEach(groupId => {
      this.removeGroupPlane(groupId);
    });
  }

  // Cập nhật khi có thay đổi
  async updateGroupPlanes() {
    //Tạo lại mặt bếp vách bếp
    await this.createAllGroupPlanes();
  }

  // Thiết lập các tham số
  setProximityThreshold(threshold) {
    this.proximityThreshold = threshold;
  }

  setRotationTolerance(tolerance) {
    this.rotationTolerance = tolerance;
  }

  setAlignmentTolerance(tolerance) {
    this.alignmentTolerance = tolerance;
  }

  setSizeTolerance(tolerance) {
    this.sizeTolerance = tolerance;
  }

  setPlanePadding(padding) {
    this.planePadding = padding;
  }

  setPlaneHeight(height) {
    this.countertopHeight = height;
  }

  setPlaneColor(color) {
    this.countertopColor = color;
  }

  // Utilities
  getModuleGroup(moduleUuid) {
    return this.moduleGroups.get(moduleUuid);
  }

  getAllGroupPlanes() {
    return Array.from(this.groupPlanes.values()).map(data => data.plane);
  }

  getGroupInfo() {
    const info = [];
    this.groupPlanes.forEach((data, groupId) => {
      info.push({
        groupId: groupId,
        moduleCount: data.modules.length,
        moduleIds: data.modules.map(m => m.uuid),
        planePosition: data.plane.position.clone(),
        boundingBox: data.boundingBox.clone(),
      });
    });
    return info;
  }

  // Dọn dẹp
  dispose() {
    this.clearAllGroupPlanes();
    this.groupPlanes.clear();
    this.moduleGroups.clear();
    this.sceneManager = null;
  }
}
